/**
 * Owner-edit authorization.
 *
 * Right now PorchLight is not broadly public — `?edit=1` alone is enough to
 * open the editor when no PIN/API is configured. Add VITE_OWNER_EDIT_PIN_HASH
 * or VITE_OWNER_LAYOUT_API later if you want a real gate.
 */

const SESSION_UNLOCK_KEY = 'porchlight-owner-edit-unlocked';
const SESSION_TOKEN_KEY = 'porchlight-owner-edit-token';

export function ownerLayoutApiBase(): string | null {
  const raw = import.meta.env.VITE_OWNER_LAYOUT_API as string | undefined;
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim().replace(/\/$/, '');
  return trimmed.length > 0 ? trimmed : null;
}

export function ownerEditPinHash(): string | null {
  const raw = import.meta.env.VITE_OWNER_EDIT_PIN_HASH as string | undefined;
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/** True when no client PIN hash is configured (API may still be set). */
export function isOpenEditGate(): boolean {
  return !ownerEditPinHash();
}

export function isOwnerEditSessionUnlocked(): boolean {
  try {
    return sessionStorage.getItem(SESSION_UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function getOwnerEditSessionToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function markOwnerEditUnlocked(token: string | null): void {
  try {
    sessionStorage.setItem(SESSION_UNLOCK_KEY, '1');
    if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    else sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* private mode */
  }
}

export function clearOwnerEditSession(): void {
  try {
    sessionStorage.removeItem(SESSION_UNLOCK_KEY);
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** SHA-256 hex of UTF-8 text (Web Crypto). */
export async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export type OwnerUnlockResult =
  | { ok: true; token: string | null; mode: 'api' | 'client-hash' | 'open' }
  | { ok: false; error: string };

/**
 * Unlock owner edit. Never stores the raw PIN.
 * - API mode: POST /unlock (server validates PIN, or open-mints when no server PIN)
 * - Client hash: compare SHA-256(PIN) to VITE_OWNER_EDIT_PIN_HASH
 * - Open gate (no API, no client hash): unlock immediately (prototype mode)
 */
export async function unlockOwnerEdit(pin: string): Promise<OwnerUnlockResult> {
  const api = ownerLayoutApiBase();
  if (api) {
    try {
      const res = await fetch(`${api}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        return {
          ok: false,
          error:
            res.status === 401 || res.status === 403
              ? 'Incorrect Editor PIN'
              : `Unlock failed (${res.status})${text ? `: ${text}` : ''}`,
        };
      }
      const body = (await res.json()) as { token?: string };
      if (!body.token) {
        return { ok: false, error: 'Unlock response missing token' };
      }
      markOwnerEditUnlocked(body.token);
      return { ok: true, token: body.token, mode: 'api' };
    } catch {
      return {
        ok: false,
        error: 'Could not reach Owner Layout API — check network / CORS',
      };
    }
  }

  const expected = ownerEditPinHash();
  if (expected) {
    const actual = await sha256Hex(pin);
    if (actual !== expected) {
      return { ok: false, error: 'Incorrect Editor PIN' };
    }
    markOwnerEditUnlocked(null);
    return { ok: true, token: null, mode: 'client-hash' };
  }

  // Prototype / unpublished site: query param is enough (local-only saves).
  markOwnerEditUnlocked(null);
  return { ok: true, token: null, mode: 'open' };
}

export function wantsOwnerEditParam(params: URLSearchParams): boolean {
  return params.has('edit') || params.has('ownerEdit');
}

/**
 * Open editor immediately (no PIN dialog) when edit query is present and no
 * client PIN hash is configured — works with or without the Layout API.
 */
export function shouldBypassPinGate(params: URLSearchParams): boolean {
  if (!wantsOwnerEditParam(params)) return false;
  return isOpenEditGate();
}

/** @deprecated Use shouldBypassPinGate */
export function shouldSkipPinForDevEdit(params: URLSearchParams): boolean {
  return shouldBypassPinGate(params);
}
